import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useGlobalContext } from "../data/GlobalContext"
import GameImage from "../snippets/GameImage"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FGHProviderItems } from "../data/ProvidersData"
import LoginToPlay from "./LoginToPlay"
// Fixed live game types
const fixedLiveTypes = [
  "Baccarat",
  "Live",
  "Roulette",
  "First Person",
  "Blackjack",
]

const gamesHavePromotion = ["pp"]
// Define the offline providers that should never be shown (all in lower-case)
const gamesOffline = ["playson"]

const MasterGamesList = ({
  category,
  limit: customLimit,
  tools,
  pagination = "off",
  rank = "off",
  linkTo = null,
  subtext,
  headerText,
  headerTag = "h1",
  randomGames = "off",
  recentgames = "off",
  topGames = "off",
  livegames = "off",
  qualifiedGamesOnly = "off",
  featuredGamesOnly = "off",
  customQualifiedGames = null,
  advantGamesOnly = "off",
  fachaiGamesOnly = "off",
  bngGamesOnly = "off",
  pragmaticGamesOnly = "off",
  customFeaturedGames = null,
  customAdvantGames = null,
  customFaChaiGames = null,
  customBNGGames = null,
  customPragmaticGames = null,
  customWrapperClass = "gameListWrapper",
  rtpTag,
}) => {
  const { allGamesFlat, tokenUrl, launchUrl, username, liveGames } =
    useGlobalContext()
  const location = useLocation()
  const { pathname } = location

  // Category mapping and order for non-live games
  const categoryMapping = {
    "-1": "Other Games",
    1: "Slot Games",
    4: "Arcade Games",
    6: "Jackpot Games",
    10: "Fishing Games",
    12: "Fishing Games",
    13: "Bingo Games",
    3: "Bingo Games",
    2: "Table Games",
    9: "Table Games",
    5: "Poker Games",
    8: "Lobby Games",
  }

  const categoryOrder = [
    "All Games",
    "New Games",
    "Favorite Games",
    "Top Games",
    "Slot Games",
    "Arcade Games",
    "Bingo Games",
    "Table Games",
    "Fishing Games",
    "Jackpot Games",
    "Poker Games",
    "Lobby Games",
    "Other Games",
  ]

  const propCategoryName = (categoryMapping[String(category)] || category || "")
    .toString()
    .toLowerCase()
  const isSlotsRoute = pathname.toLowerCase().includes("/slots")
  const isSlotsPage = propCategoryName === "slot games" || isSlotsRoute

  // Helper function to return the mapped category
  const getMappedCategory = (game) => {
    return (
      categoryMapping[String(game.gameCategory)] || String(game.gameCategory)
    )
  }

  const [currentPage, setCurrentPage] = useState(0)
  const [selectedProvider, setSelectedProvider] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [showPromotionGames, setShowPromotionGames] = useState(false)
  const [displayLimit, setDisplayLimit] = useState(customLimit || 12)
  const [recentGamesList, setRecentGamesList] = useState([])
  const [login, setLogin] = useState(false)

  const [favorites, setFavorites] = useState(() => {
    return JSON.parse(localStorage.getItem("favoriteGames")) || []
  })

  useEffect(() => {
    localStorage.setItem("favoriteGames", JSON.stringify(favorites))
  }, [favorites])

  const toggleFavorite = (gameId) => {
    setFavorites((prev) =>
      prev.includes(gameId)
        ? prev.filter((id) => id !== gameId)
        : [...prev, gameId]
    )
  }

  const limit = customLimit || 12

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const providerFromQuery = params.get("provider")
    const categoryFromQuery = params.get("category")

    if (providerFromQuery) {
      setSelectedProvider(providerFromQuery)
    }
    if (categoryFromQuery) {
      setSelectedCategory(categoryFromQuery)
    }
    // If the search includes "top-games", this will override any previous category setting.
    if (location.search.toLowerCase().includes("top-games")) {
      setSelectedCategory("Top Games")
    }
  }, [location])

  useEffect(() => {
    if (category) setSelectedCategory(category)
  }, [category])

  // Handle recentgames filtering.
  // Handle recentgames filtering.
  useEffect(() => {
    if (["1", "2", "3", "all", "showall"].includes(recentgames)) {
      const stored = JSON.parse(localStorage.getItem("recentGames")) || []
      const games = stored
        .map((id) => allGamesFlat.find((game) => game.gameId === id))
        .filter(Boolean)
      setRecentGamesList(games)
    }
  }, [recentgames, allGamesFlat])

  const updateRecentGames = (gameId) => {
    const stored = JSON.parse(localStorage.getItem("recentGames")) || []
    // Remove duplicate if it exists.
    const filtered = stored.filter((id) => id !== gameId)
    filtered.unshift(gameId)

    if (recentgames === "all") {
      localStorage.setItem("recentGames", JSON.stringify(filtered))
    } else {
      localStorage.setItem("recentGames", JSON.stringify(filtered.slice(0, 6)))
    }

    // Update the state immediately so the UI refreshes.
    const games = JSON.parse(localStorage.getItem("recentGames"))
      .map((id) => allGamesFlat.find((game) => game.gameId === id))
      .filter(Boolean)
    setRecentGamesList(games)
  }

  if (recentgames === "all" && recentGamesList.length === 0) {
    return null
  }
  if (
    ["1", "2", "3"].includes(recentgames) &&
    !recentGamesList[parseInt(recentgames, 10) - 1]
  ) {
    return null
  }

  const placeholderGames = Array.from({ length: limit }, (_, index) => ({
    gameId: `placeholder-${index}`,
    gameName: "",
    provider: "...",
    rank: index + 1,
    placeholder: true,
  }))

  // For non-live games, apply your normal filtering.
  const filteredGamesByCategory =
    category && category !== "All Games"
      ? allGamesFlat.filter((game) => getMappedCategory(game) === category)
      : allGamesFlat

  const filteredGamesByProvider = selectedProvider
    ? filteredGamesByCategory.filter(
        (game) =>
          game.provider?.toLowerCase() === selectedProvider.toLowerCase()
      )
    : filteredGamesByCategory

  const filteredGamesByPromotion = showPromotionGames
    ? filteredGamesByProvider.filter((game) =>
        gamesHavePromotion.includes(game.provider?.toLowerCase())
      )
    : filteredGamesByProvider

  const filteredGamesByTop =
    topGames === "on"
      ? filteredGamesByPromotion.filter(
          (game) =>
            String(game.gameCategory || "").toLowerCase() === "top games"
        )
      : filteredGamesByPromotion

  const filteredGamesBySelection =
    selectedCategory && selectedCategory !== "All Games"
      ? filteredGamesByTop.filter((game) => {
          const cat = selectedCategory.toLowerCase()

          // if user picked "Favorite Games", only include games in favorites
          if (cat === "favorite games") {
            return favorites.includes(game.gameId)
          }

          // existing Top Games logic
          if (cat === "top games") {
            return game.isTopGame
          }

          if (cat === "new games") {
            return game.isNewGame
          }

          // existing Bingo Games logic
          if (cat === "bingo games") {
            return getMappedCategory(game).toLowerCase().includes("bingo")
          }

          // fallback to matching mapped category
          return getMappedCategory(game).toLowerCase().includes(cat)
        })
      : filteredGamesByTop

  let baseGames

  // showall: put all recent first, then the rest
  if (
    recentgames === "showall" &&
    (!selectedCategory || selectedCategory.toLowerCase() === "all games")
  ) {
    let filteredRecent = recentGamesList

    // ✅ Filter recent games by selected provider
    if (selectedProvider) {
      filteredRecent = filteredRecent.filter(
        (game) =>
          game.provider &&
          game.provider.toLowerCase() === selectedProvider.toLowerCase()
      )
    }

    const recentIds = filteredRecent.map((g) => g.gameId)

    const remaining = filteredGamesBySelection.filter(
      (game) => !recentIds.includes(game.gameId)
    )

    baseGames = [...filteredRecent, ...remaining]
  } else if (livegames === "on") {
    baseGames = liveGames.map((game, index) => ({
      gameId: game.gameId || game.id || `live-${index}`,
      gameName: game.gameName || game.name || "Live Game",
      provider: game.provider,
      gameImg: game.img || game.gameImg,
      type: game.type,
    }))

    if (customQualifiedGames && customQualifiedGames.length > 0) {
      baseGames = baseGames.filter((game) =>
        customQualifiedGames.some(
          (qualified) =>
            game.provider?.toLowerCase() === qualified.provider.toLowerCase() &&
            game.gameName?.toLowerCase() === qualified.gameName.toLowerCase()
        )
      )
    }

    if (selectedProvider) {
      baseGames = baseGames.filter(
        (game) =>
          game.provider &&
          game.provider.toLowerCase() === selectedProvider.toLowerCase()
      )
    }
    if (selectedCategory) {
      baseGames = baseGames.filter((game) => {
        return Array.isArray(game.type)
          ? game.type.some(
              (t) => t.toLowerCase() === selectedCategory.toLowerCase()
            )
          : game.type.toLowerCase() === selectedCategory.toLowerCase()
      })
    }
  } else if (recentgames === "all") {
    if (recentGamesList.length === 0) return null
    baseGames = recentGamesList
  } else if (["1", "2", "3"].includes(recentgames)) {
    const recentIndex = parseInt(recentgames, 10) - 1
    const baseGame = recentGamesList[recentIndex]
    if (!baseGame) return null
    baseGames = [baseGame]
    if (baseGames.length < displayLimit) {
      const keywords = baseGame.gameName.toLowerCase().split(/\s+/)
      const additionalGames = allGamesFlat.filter((game) => {
        const nameLower = game.gameName.toLowerCase()
        if (recentGamesList.find((rg) => rg.gameId === game.gameId))
          return false
        return keywords.some((kw) => nameLower.includes(kw))
      })
      baseGames = [...baseGames, ...additionalGames]
    }
  } else {
    baseGames =
      randomGames === "on"
        ? [...filteredGamesBySelection].sort(() => Math.random() - 0.5)
        : filteredGamesBySelection
  }

  baseGames = baseGames.sort((a, b) => {
    const aBoost = a.topGames === "topnew" ? 1 : 0
    const bBoost = b.topGames === "topnew" ? 1 : 0
    return bBoost - aBoost
  })

  baseGames = baseGames.filter(
    (game) =>
      game.provider && !gamesOffline.includes(game.provider.toLowerCase())
  )

  const effectiveQualifiedGames = customQualifiedGames || [
    { provider: "pp", gameName: "Gates of Olympus Super Scatter" },
    { provider: "pp", gameName: "Sweet Bonanza Super Scatter" },
    { provider: "pp", gameName: "Zombie School Megaways" },
    { provider: "pp", gameName: "Jackpot Blaze" },
    { provider: "pp", gameName: "Sweet Rush Bonanza" },
  ]

  if (qualifiedGamesOnly === "on") {
    baseGames = effectiveQualifiedGames
      .map((featured) => {
        const matchedGame = allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )

        return matchedGame
          ? {
              ...matchedGame,
              rtpTag: featured.rtpTag || "",
            }
          : null
      })
      .filter(Boolean)
  }

  const effectiveFaChaiGames = customFaChaiGames || [
    { provider: "fachai", gameName: "CHINESE NEW YEAR 2" },
    { provider: "fachai", gameName: "JUNGLE BANG BANG" },
    { provider: "fachai", gameName: "CHINESE NEW YEAR MOREWAYS" },
  ]

  if (fachaiGamesOnly === "on") {
    baseGames = effectiveFaChaiGames
      .map((featured) =>
        allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )
      )
      .filter(Boolean)
  }

  const effectiveBNGGames = customBNGGames || [
    { provider: "bng", gameName: "More Magic Apple" },
    { provider: "bng", gameName: "3 Carts of Gold" },
    { provider: "bng", gameName: "3 Hot Chillies" },
    { provider: "bng", gameName: "Grab more Gold!" },
  ]

  if (bngGamesOnly === "on") {
    baseGames = effectiveBNGGames
      .map((featured) =>
        allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )
      )
      .filter(Boolean)
  }

  const effectiveAdvantGames = customAdvantGames || [
    { provider: "jolliplay", gameName: "Mahjong Roar" },
    { provider: "jolliplay", gameName: "LABUBU: Fall In Wild" },
    { provider: "jolliplay", gameName: "Maya: Elemental Totem 2" },
    { provider: "jolliplay", gameName: "Big Net Bass" },
    { provider: "jolliplay", gameName: "Disco 777 Hold and Win" },
    { provider: "jolliplay", gameName: "Sugar Crush" },
    { provider: "jolliplay", gameName: "Piggy Bank Gold" },
    { provider: "jolliplay", gameName: "Squid Game 2：Mukunghwa" },
    { provider: "jolliplay", gameName: "Leprechaun’s Fortune" },
    { provider: "jolliplay", gameName: "BlackJack 21" },
    { provider: "jolliplay", gameName: "Pirate Treasure Hunt" },
    { provider: "jolliplay", gameName: "Cai Shen Fortune" },
    { provider: "jolliplay", gameName: "Aztec: Gold Temple" },
    { provider: "jolliplay", gameName: "Crazy Bounty: Jackpot" },
    { provider: "jolliplay", gameName: "Rush Hour Gold" },
    { provider: "jolliplay", gameName: "Buffalo Rush" },
    { provider: "jolliplay", gameName: "Rhapsody of Muertos" },
    { provider: "jolliplay", gameName: "Phantom Multiplier" },
    { provider: "jolliplay", gameName: "Starry Adventure" },
    { provider: "jolliplay", gameName: "Fish Prawn Crab Bonanza" },
    { provider: "jolliplay", gameName: "Aztec: Bonus Hunt 2" },
    { provider: "jolliplay", gameName: "Football Fever" },
    { provider: "jolliplay", gameName: "Firefly Hunter" },
    { provider: "jolliplay", gameName: "Dark Ritual" },
    { provider: "jolliplay", gameName: "Hungry Slime" },
    { provider: "jolliplay", gameName: "Maya: Elemental Totem" },
    { provider: "jolliplay", gameName: "Dragon Chi’s Quest" },
    { provider: "jolliplay", gameName: "Last Samurai" },
  ]

  if (advantGamesOnly === "on") {
    baseGames = effectiveAdvantGames
      .map((featured) =>
        allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )
      )
      .filter(Boolean)
  }

  const effectivePragmaticGames = customPragmaticGames || [
    { provider: "tpg", gameName: "Halloween Hold & Spin" },
  ]

  if (pragmaticGamesOnly === "on") {
    baseGames = effectivePragmaticGames
      .map((featured) =>
        allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )
      )
      .filter(Boolean)
  }

  // Force CHINESE NEW YEAR MEGAWAYS / MOREWAYS into Top 5 when All Games + All
  // if (
  //   (!selectedCategory || selectedCategory.toLowerCase() === "all games") &&
  //   !selectedProvider
  // ) {
  //   // Chinese New Year Megaways / Moreways
  //   const chineseGame = allGamesFlat.find(
  //     (g) =>
  //       g.gameName &&
  //       g.gameName.toLowerCase().includes("chinese new year") &&
  //       (g.gameName.toLowerCase().includes("megaways") ||
  //         g.gameName.toLowerCase().includes("moreways"))
  //   )

  //   if (chineseGame) {
  //     baseGames = baseGames.filter((g) => g.gameId !== chineseGame.gameId)
  //     baseGames.splice(4, 0, chineseGame) // Top 5 spot
  //   }

  //   // Sugar Bang Bang
  //   const sugarGame = allGamesFlat.find(
  //     (g) => g.gameName && g.gameName.toLowerCase() === "sugar bang bang"
  //   )

  //   if (sugarGame) {
  //     baseGames = baseGames.filter((g) => g.gameId !== sugarGame.gameId)
  //     baseGames.splice(4, 0, sugarGame)
  //   }
  // }

  // Default featured games if none provided
  const effectiveFeaturedGames = customFeaturedGames || [
    { provider: "jili", gameName: "Super Ace" },
    { provider: "pp", gameName: "Fortune of Olympus" },
    { provider: "tpg", gameName: "God of Wealth" },
    { provider: "pp", gameName: "Sweet Bonanza 1000" },
    { provider: "betsoft", gameName: "Deuces Wild" },
  ]

  if (featuredGamesOnly === "on") {
    baseGames = effectiveFeaturedGames
      .map((featured) =>
        allGamesFlat.find(
          (game) =>
            game.provider.toLowerCase() === featured.provider &&
            game.gameName.toLowerCase() === featured.gameName.toLowerCase()
        )
      )
      .filter(Boolean)
  }
  // End of top featured games

  const gamesToDisplay =
    baseGames.length > 0
      ? pagination === "on"
        ? baseGames.slice(currentPage * limit, currentPage * limit + limit)
        : baseGames.slice(0, displayLimit)
      : placeholderGames

  const handlePagination = (direction) => {
    const totalPages = Math.ceil(baseGames.length / limit)
    let newPage = currentPage + direction
    if (newPage < 0) newPage = totalPages - 1
    else if (newPage >= totalPages) newPage = 0
    setCurrentPage(newPage)
  }

  const launchGameInternal = async (gameId) => {
    try {
      const requestUrl = `${tokenUrl}?u=${username}&g=${gameId}&t=transfer&m=mobile`
      const response = await fetch(requestUrl)
      if (!response.ok) {
        alert("Failed to fetch game token. Please try again.")
        return
      }
      const data = await response.json()

      const sessionToken = data?.token
      if (!sessionToken) {
        alert("Token not received. Please try again.")
        return
      }
      updateRecentGames(gameId)
      const lang = "en"
      const mobileIndicator = window.innerWidth <= 768 ? "1" : ""
      const isMobile = window.innerWidth <= 768
      const currentPageUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`
      const encodedCurrentPageUrl = encodeURIComponent(currentPageUrl)
      const gameUrl = `${launchUrl}?s=${sessionToken}&l=${lang}&m=${mobileIndicator}&h=${encodedCurrentPageUrl}`

      // const gameUrl = `${launchUrl}?s=${sessionToken}&l=${lang}&m=${mobileIndicator}`

      // Open game in a new separate window with custom dimensions and features.
      if (isMobile) {
        // On mobile, navigate in the same tab
        window.location.href = gameUrl
      } else {
        // On desktop, open in a separate popup
        window.open(
          gameUrl,
          "gameWindow",
          "width=1024,height=768,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes"
        )
      }
    } catch (error) {
      console.error("Error launching game:", error)
      alert("An unexpected error occurred. Please try again.")
    }
  }

  // Modified header text for recent games.
  const HeaderTag = headerTag
  const defaultHeaderText =
    recentgames === "all"
      ? "Your Recent Games"
      : ["1", "2", "3"].includes(recentgames)
        ? recentgames === "1"
          ? `Since you played ${recentGamesList[0]?.gameName || "the game"}`
          : recentgames === "2"
            ? `Continue playing ${recentGamesList[1]?.gameName || "the game"}`
            : `Play ${recentGamesList[2]?.gameName || "the game"} again`
        : selectedProvider
          ? FGHProviderItems.find(
              (provider) =>
                provider.provider_name.toLowerCase() ===
                selectedProvider.toLowerCase()
            )?.provider_message || selectedProvider + " Games"
          : category
            ? `${category} Games`
            : "Top Games for You, Play Now & Win Big"

  const showRank =
    selectedCategory && selectedCategory.toLowerCase() === "top games"
      ? "on"
      : rank

  // ***** EXCLUDE OFFLINE PROVIDERS from dropdown filters *****
  const liveProviderList =
    livegames === "on" && pathname.includes("/live-casino")
      ? [
          ...new Set(
            liveGames
              .filter(
                (game) =>
                  game.provider &&
                  !gamesOffline.includes(game.provider.toLowerCase())
              )
              .map((game) => game.provider)
          ),
        ]
      : []

  const regularProviderList =
    livegames !== "on"
      ? (() => {
          const providers = [
            ...new Set(
              allGamesFlat
                .filter(
                  (game) =>
                    game.provider &&
                    !gamesOffline.includes(game.provider.toLowerCase())
                )
                .map((game) => game.provider)
            ),
          ]

          // Prioritize pp and cq9 manually
          const customOrder = ["pp", "cq9"]
          const orderedProviders = []

          customOrder.forEach((p) => {
            const index = providers.findIndex(
              (provider) => provider.toLowerCase() === p
            )
            if (index > -1) {
              orderedProviders.push(providers.splice(index, 1)[0])
            }
          })

          // Append the rest
          return [...orderedProviders, ...providers]
        })()
      : []

  // For live games, use fixed types for the dropdown.
  const liveTypeList =
    livegames === "on" && pathname.includes("/live-casino")
      ? fixedLiveTypes
      : []

  const showLogin = () => {
    setLogin(true)
  }

  const GameList = ({ games }) => (
    <div className={customWrapperClass}>
      {games.map((game, index) => (
        <div key={game.gameID || `game-${index}`} className="gameWrapper">
          {game.rtpTag && <div className="rtp-tag">{game.rtpTag}</div>}
          <div className="game">
            {showRank === "on" && (
              <span className="rank-label">
                Top <br /> {currentPage * limit + index + 1}
              </span>
            )}
            <GameImage gameId={game.gameId} gameImg={game.gameImg} />
            <div className="gameDetails">
              {livegames !== "on" && isSlotsPage && (
                <button
                  type="button"
                  className="favorite-btn"
                  onClick={() => toggleFavorite(game.gameId)}
                >
                  {favorites.includes(game.gameId) ? "♥︎" : "♡"}
                </button>
              )}
              <div className="gameName">{game.gameName}</div>
              <div className="gameProvider">{game.provider || "Unknown"}</div>
              {game.placeholder ? (
                <div className="game-loading-placeholder"></div>
              ) : username ? (
                <button
                  type="button"
                  className="fgh-play-now-btn"
                  onClick={() => launchGameInternal(game.gameId)}
                >
                  Play Now
                </button>
              ) : (
                <button
                  type="button"
                  className="fgh-play-now-btn"
                  onClick={showLogin}
                >
                  Play Now
                </button>
              )}
            </div>
            <div className="game-overlay"></div>
          </div>
        </div>
      ))}
    </div>
  )

  const formatMessage = () => {
    if (selectedCategory) {
      return selectedCategory.replace(/games$/i, "").toLowerCase()
    } else if (selectedProvider) {
      return selectedProvider.toLowerCase()
    }
    return "matching"
  }

  const gameLoading = [
    { name: "...", provider: "..." },
    { name: "...", provider: "..." },
    { name: "...", provider: "..." },
  ]
  return (
    <div>
      {login && <LoginToPlay onClose={() => setLogin(false)} />}

      <div className="fgh-headlines-controls">
        {headerText !== null && headerText !== "" && (
          <div className="fgh-headline-text">
            <HeaderTag
              className={headerTag === "h1" ? "fgh-h1-style" : "fgh-h2-style"}
            >
              {headerText || defaultHeaderText}
            </HeaderTag>
            {subtext && <p className="fgh-subtext">{subtext}</p>}
          </div>
        )}
        <div className="fgh-games-main-tools-container">
          <div className="fgh-games-tools">
            {tools === "on" && (
              <>
                {pathname.includes("/live-casino") ? (
                  <>
                    <div className="fgh-selection-container">
                      <img src="/images/icon/filter-icon.webp" alt="Filter" />
                      <select
                        value={selectedProvider}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="fgh-provider-select"
                      >
                        <option value="">All</option>
                        {liveProviderList.map((provider) => (
                          <option key={provider} value={provider}>
                            {provider}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="fgh-selection-container">
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="fgh-category-select"
                      >
                        <option value="">Select Type</option>
                        {liveTypeList.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fgh-selection-container">
                      <img src="/images/icon/filter-icon.webp" alt="Filter" />
                      <select
                        value={selectedProvider}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="fgh-provider-select"
                      >
                        <option value="">All</option>
                        {regularProviderList.map((provider) => (
                          <option key={provider} value={provider}>
                            {provider}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="fgh-selection-container">
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="fgh-category-select"
                      >
                        {categoryOrder.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          {pagination === "on" && (
            <div className="fgh-main-pagination">
              <button
                className={`fgh-main-pagination-btn ${
                  currentPage === 0 ? "disabled" : ""
                }`}
                onClick={() => handlePagination(-1)}
                disabled={currentPage === 0}
              >
                &larr;
              </button>
              <button
                className="fgh-main-pagination-btn"
                onClick={() => handlePagination(1)}
                disabled={(currentPage + 1) * limit >= baseGames.length}
              >
                &rarr;
              </button>
            </div>
          )}
          {linkTo && (
            <a href={linkTo} className="fgh-main-pagination-btn">
              <FontAwesomeIcon
                icon={faChevronRight}
                className="custom-next-btn"
              />
            </a>
          )}
        </div>
      </div>

      {/* {selectedCategory.toLowerCase() === "new games" &&
      baseGames.length === 0 ? (
        <div className="fgh-no-favorites">
          <img src="/images/character/FGH_CHARACTER-CONFUSED.webp" alt="" />
          <p>No new games are available at the moment.</p>
        </div>
      ) : selectedCategory.toLowerCase() === "favorite games" &&
        baseGames.length === 0 ? (
        <div className="fgh-no-favorites">
          <img src="/images/character/FGH_CHARACTER-CONFUSED.webp" alt="" />
          <p>No games have been marked as favorites.</p>
        </div>
      ) : selectedCategory.toLowerCase() === "top games" &&
        baseGames.length === 0 ? (
        <div className="fgh-no-favorites">
          <img src="/images/character/FGH_CHARACTER-CONFUSED.webp" alt="" />
          <p>No top games are available at the moment.</p>
        </div>
      ) : (
        <GameList games={gamesToDisplay} />
      )} */}

      {allGamesFlat.length === 0 ? (
        <>
          <div className="gameListWrapper">
            {gameLoading.map((game, i) => (
              <div className="gameWrapper" key={i}>
                <div className="game">
                  <div className="gameDetails">
                    <div className="gameProvider">{game.provider}</div>
                  </div>
                  <div className="game-overlay"></div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : baseGames.length === 0 ? (
        <div className="fgh-no-favorites">
          <img src="/images/character/FGH_CHARACTER-DISAPPOINTED.webp" alt="" />
          <p>No {formatMessage()} games are available at the moment.</p>
        </div>
      ) : (
        <GameList games={gamesToDisplay} />
      )}

      {tools === "on" &&
        pagination === "off" &&
        baseGames.length > displayLimit && (
          <div className="view-more-container">
            <button
              className="view-more-btn"
              onClick={() =>
                setDisplayLimit((prev) => Math.min(prev + 12, baseGames.length))
              }
            >
              View More
            </button>
          </div>
        )}
    </div>
  )
}

export default MasterGamesList
